import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Group,
  Select,
  Stack,
  Text,
  Alert,
  Loader,
  Center,
  Card,
  Table,
  Title,
  Box,
  ColorInput,
  ScrollArea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconDeviceFloppy, IconPalette } from '@tabler/icons-react';
import classes from './StatusColourManagement.module.css';
import { getStatusByAddressSystem, updateStatusColour } from '../../services/MapComponentsService';
import { GeometryStatus } from '../../types/BaseGeometry';
import useTransientStore from '../../data/transcientStore';
import { useTranslation } from '../../hooks/useTranslation';

interface StatusColourState {
  statusId: number;
  colour: number[];
  originalColour: number[];
  hasChanged: boolean;
}

/**
 * Converts RGB array [R, G, B] to hex colour string
 */
function rgbToHex(rgb: number[] | null): string {
  if (!rgb || rgb.length !== 3) return '#000000';
  const [r, g, b] = rgb;
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Converts hex colour string to RGB array [R, G, B]
 */
function hexToRgb(hex: string): number[] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 0, 0];
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ];
}

export default function StatusColourManagement() {
  // Get user's available address systems from store
  const currentUserAddressSystems = useTransientStore((state) => state.currentUserAddressSystems);
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;
  const { getTranslation } = useTranslation({
    pageId: 352,
    addressSystemId: addrIdFromStore,
    autoLoad: true,
  });

  const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<GeometryStatus[]>([]);
  const [colourStates, setColourStates] = useState<StatusColourState[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load statuses when address system is selected
  useEffect(() => {
    const loadStatuses = async () => {
      if (!selectedAddressSystemId) {
        setStatuses([]);
        setColourStates([]);
        return;
      }

      setLoading(true);
      try {
        const statusData = await getStatusByAddressSystem(parseInt(selectedAddressSystemId));
        setStatuses(statusData);
        
        // Initialize colour states
        setColourStates(statusData.map(status => ({
          statusId: status.id,
          colour: status.colour ?? [0, 0, 0],
          originalColour: status.colour ?? [0, 0, 0],
          hasChanged: false
        })));
      } catch (error) {
        notifications.show({
          title: getTranslation('LABEL_SC_ERROR'),
          message: getTranslation('LABEL_SC_LOAD_ERROR'),
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    loadStatuses();
  }, [selectedAddressSystemId]);

  // Handle colour change for a status
  const handleColourChange = useCallback((statusId: number, hexColour: string) => {
    const newRgb = hexToRgb(hexColour);
    
    setColourStates(prev => prev.map(state => {
      if (state.statusId !== statusId) return state;
      
      const hasChanged = 
        newRgb[0] !== state.originalColour[0] ||
        newRgb[1] !== state.originalColour[1] ||
        newRgb[2] !== state.originalColour[2];
      
      return {
        ...state,
        colour: newRgb,
        hasChanged
      };
    }));
  }, []);

  // Check if there are any unsaved changes
  const hasChanges = colourStates.some(state => state.hasChanged);

  // Save all changed colours
  const handleSave = async () => {
    const changedStates = colourStates.filter(state => state.hasChanged);
    
    if (changedStates.length === 0) return;

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const state of changedStates) {
        try {
          await updateStatusColour(state.statusId, state.colour);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to update colour for status ${state.statusId}:`, error);
        }
      }

      if (successCount > 0) {
        notifications.show({
          title: getTranslation('LABEL_SC_SUCCESS'),
          message: `${getTranslation('LABEL_SC_UPDATED')} ${successCount} ${getTranslation('LABEL_SC_STATUS_COLOURS_COUNT')}`,
          color: 'green',
          withBorder: true,
          withCloseButton: true,
          autoClose: 3000,
        });

        // Update original colours to match saved values
        setColourStates(prev => prev.map(state => ({
          ...state,
          originalColour: state.colour,
          hasChanged: false
        })));

        // Also refresh the global status colours in the store if the current address system is selected
        const selectedAddressSystem = useTransientStore.getState().selectedAddressSystem;
        if (selectedAddressSystem && selectedAddressSystem.id.toString() === selectedAddressSystemId) {
          const refreshedStatuses = await getStatusByAddressSystem(parseInt(selectedAddressSystemId));
          useTransientStore.getState().setSelectedAddressSystemStatuses(refreshedStatuses);
        }
      }

      if (errorCount > 0) {
        notifications.show({
          title: getTranslation('LABEL_SC_WARNING'),
          message: `${getTranslation('LABEL_SC_UPDATE_FAILED')} ${errorCount} ${getTranslation('LABEL_SC_STATUS_COLOURS_COUNT')}`,
          color: 'orange',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
        });
      }
    } catch (error) {
      notifications.show({
        title: getTranslation('LABEL_SC_ERROR'),
        message: getTranslation('LABEL_SC_SAVE_ERROR'),
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Reset all changes
  const handleReset = () => {
    setColourStates(prev => prev.map(state => ({
      ...state,
      colour: state.originalColour,
      hasChanged: false
    })));
  };

  // Get colour state by status ID
  const getColourState = (statusId: number) => colourStates.find(s => s.statusId === statusId);

  return (
    <div className={classes.container}>
      <Card className={classes.cardContainer} withBorder shadow="sm" radius="md">
        <Stack className={classes.stackContainer}>
          <Group justify="space-between" align="center">
            <Title order={3}>
              <Group gap="xs">
                <IconPalette size={24} />
                {getTranslation('LABEL_SC_TITLE')}
              </Group>
            </Title>
          </Group>

          <Text c="dimmed" size="sm">
            {getTranslation('LABEL_SC_DESCRIPTION')}
          </Text>

          <Select
            label={getTranslation('LABEL_SC_ADDRESS_SYSTEM') ?? undefined}
            placeholder={getTranslation('LABEL_SC_SELECT_ADDRESS_SYSTEM') ?? undefined}
            data={currentUserAddressSystems.map((system) => ({
              value: system.id.toString(),
              label: system.name
            }))}
            value={selectedAddressSystemId}
            onChange={setSelectedAddressSystemId}
            clearable
            searchable
            w={350}
          />

          {loading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}

          {!loading && !selectedAddressSystemId && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={getTranslation('LABEL_SC_NO_ADDRESS_SYSTEM_SELECTED')}
              color="blue"
              variant="light"
            >
              {getTranslation('LABEL_SC_SELECT_ADDRESS_SYSTEM_PROMPT')}
            </Alert>
          )}

          {!loading && selectedAddressSystemId && statuses.length === 0 && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={getTranslation('LABEL_SC_NO_STATUSES_FOUND')}
              color="yellow"
              variant="light"
            >
              {getTranslation('LABEL_SC_NO_STATUSES_MESSAGE')}
            </Alert>
          )}

          {!loading && selectedAddressSystemId && statuses.length > 0 && (
            <>
              <ScrollArea className={classes.contentSection} offsetScrollbars>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={80}>{getTranslation('LABEL_SC_PREVIEW')}</Table.Th>
                      <Table.Th>{getTranslation('LABEL_SC_STATUS_NAME')}</Table.Th>
                      <Table.Th>{getTranslation('LABEL_SC_STATUS_DESCRIPTION')}</Table.Th>
                      <Table.Th w={180}>{getTranslation('LABEL_SC_COLOUR')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {statuses.map((status) => {
                      const colourState = getColourState(status.id);
                      const hexColour = colourState ? rgbToHex(colourState.colour) : '#000000';
                      
                      return (
                        <Table.Tr 
                          key={status.id}
                          style={{
                            backgroundColor: colourState?.hasChanged ? 'var(--mantine-color-yellow-0)' : undefined
                          }}
                        >
                          <Table.Td>
                            <Box
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: hexColour,
                                border: '1px solid #ccc',
                                borderRadius: 4,
                              }}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text fw={500}>{status.name}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {status.description || '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <ColorInput
                              value={hexColour}
                              onChange={(value) => handleColourChange(status.id, value)}
                              format="hex"
                              swatches={[
                                '#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF',
                                '#4B0082', '#9400D3', '#FF1493', '#00CED1', '#32CD32',
                                '#FFD700', '#8B4513', '#808080', '#000000', '#FFFFFF'
                              ]}
                              size="sm"
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              <Group justify="flex-end" mt="md">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={!hasChanges || saving}
                >
                  {getTranslation('LABEL_SC_RESET_CHANGES')}
                </Button>
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={!hasChanges}
                >
                  {getTranslation('LABEL_SC_SAVE_CHANGES')}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>
    </div>
  );
}
